// src/utils/managedFieldRuntime.js
// -----------------------------------------------------------------------------
// Runtime resolver layer for org-managed dropdown field instances.
//
// PURPOSE
//  • Resolve org-managed option sets into the SAME dropdown option shape your
//    current runtime components already expect
//  • Keep lifecycle / governance filtering OUT of field UI components
//  • Provide a safe bridge between:
//      - org-managed field definitions / option sets
//      - form-local field instances
//      - existing dropdown rendering pipeline
//
// IMPORTANT PRINCIPLES
//  • This file is runtime-facing, but still domain/backend-oriented
//  • It does NOT touch React context directly
//  • It does NOT read/write browser storage
//  • It does NOT compress/decompress anything
//  • It does NOT mutate Firestore or local form state
//  • It simply resolves / validates org-managed dropdown runtime payloads
//
// WHY THIS FILE EXISTS
//  • MultiAnswerFieldContext currently expects dropdownOptions keyed by optionsSetUFI
//  • SingleSelectFieldComponent / MultiSelectFieldComponent expect plain arrays
//    of { optionId, label, value, hint, order }
//  • Org-managed dropdowns must flow into that same contract without forking UI
//
// ALIGNMENT NOTES
//  • Your current runtime expects dropdown option arrays in the shape:
//      { optionId, label, value, hint, order }
//  • Your current field metadata expects optionsSetUFI to be the lookup key
//  • Your current dependency graph remains form-scoped and references optionIds
//  • This file therefore resolves org-managed option sets into a runtime-safe map
//    keyed by optionsSetUFI, while preserving enough diagnostics to support:
//      - dependency health checks
//      - preset validation
//      - admin/builder warnings for inactive/missing options
//
// NOTES
//  • Callers can later use these helpers inside MultiAnswerFieldContext or
//    another orchestrator layer to merge local and org-managed option maps.
//  • This file is intentionally additive and safe for enterprise rollout.
// -----------------------------------------------------------------------------

import {
  getManagedOptionSetById,
  resolveLiveManagedOptionsForRuntime,
} from './managedFields';

import {
  isOrgManagedDropdownInstance,
} from './managedFieldInstances';

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

const normalizeString = (value, fallback = '') => {
  return typeof value === 'string' ? value.trim() : fallback;
};

const normalizeNullableString = (value) => {
  const s = normalizeString(value, '');
  return s.length > 0 ? s : null;
};

const isObject = (value) => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const toArraySafe = (value) => {
  return Array.isArray(value) ? value : [];
};

const buildFieldMapByFieldId = (fieldsMetadata = {}) => {
  if (!isObject(fieldsMetadata)) return {};
  return { ...fieldsMetadata };
};

const buildFieldMapByUfi = (fieldsMetadata = {}) => {
  const result = {};

  if (!isObject(fieldsMetadata)) return result;

  Object.values(fieldsMetadata).forEach((meta) => {
    const ufi = normalizeString(meta?.ufi);
    if (!ufi) return;
    result[ufi] = meta;
  });

  return result;
};

const buildManagedDropdownCandidates = (fieldsMetadata = {}) => {
  if (!isObject(fieldsMetadata)) return [];

  return Object.values(fieldsMetadata).filter((meta) =>
    isOrgManagedDropdownInstance(meta)
  );
};

const buildOptionLookupFromRuntimeOptions = (runtimeOptions = []) => {
  const byId = new Map();

  toArraySafe(runtimeOptions).forEach((opt) => {
    const optionId = normalizeString(opt?.optionId);
    if (!optionId) return;

    byId.set(optionId, opt);
  });

  return byId;
};

const isMultiSelectFieldType = (fieldMetadata) => {
  return normalizeString(fieldMetadata?.type) === 'multi-select';
};

// -----------------------------------------------------------------------------
// Public helpers
// -----------------------------------------------------------------------------

/**
 * Lightweight introspection helper:
 * returns a normalized descriptor for a managed dropdown field instance.
 *
 * This is useful for logging, debugging, and future builder/runtime diagnostics.
 */
export const describeManagedDropdownInstance = (fieldMetadata) => {
  if (!isOrgManagedDropdownInstance(fieldMetadata)) {
    return null;
  }

  return {
    fieldId: normalizeString(fieldMetadata?.id),
    ufi: normalizeString(fieldMetadata?.ufi),
    type: normalizeString(fieldMetadata?.type),
    label: normalizeString(fieldMetadata?.label),
    managedFieldId: normalizeString(fieldMetadata?.managedFieldId),
    managedFieldVersion: Number.isFinite(
      Number(fieldMetadata?.managedFieldVersion)
    )
      ? Number(fieldMetadata.managedFieldVersion)
      : 1,
    managedOptionSetId: normalizeString(fieldMetadata?.managedOptionSetId),
    optionsSetUFI: normalizeString(fieldMetadata?.optionsSetUFI),
    optionsSource: normalizeString(fieldMetadata?.optionsSource),
    orgManaged: fieldMetadata?.orgManaged === true,
    semanticKey: normalizeNullableString(fieldMetadata?.semanticKey),
    assetFieldRole: normalizeNullableString(fieldMetadata?.assetFieldRole),
  };
};

/**
 * Resolve a single managed dropdown field instance to its active runtime options.
 *
 * OUTPUT
 *  {
 *    status: 'success' | 'not_found' | 'not_applicable' | 'error',
 *    field: { ...descriptor } | null,
 *    optionsSetUFI,
 *    managedOptionSetId,
 *    options: [{ optionId, label, value, hint, order }],
 *    rawOptionSet: { ... } | null,
 *    diagnostics: { ... }
 *  }
 *
 * DESIGN CHOICE
 *  • We expose both:
 *      - runtime-safe options
 *      - raw option set document
 *    so later callers can support:
 *      - admin diagnostics
 *      - preset validation
 *      - dependency health warnings
 */
export const resolveManagedDropdownInstanceOptions = async ({
  organizationId,
  fieldMetadata,
  now = new Date(),
}) => {
  if (!normalizeString(organizationId)) {
    throw new Error(
      '[managedFieldRuntime] organizationId is required to resolve managed dropdown options.'
    );
  }

  if (!isOrgManagedDropdownInstance(fieldMetadata)) {
    return {
      status: 'not_applicable',
      field: null,
      optionsSetUFI: null,
      managedOptionSetId: null,
      options: [],
      rawOptionSet: null,
      diagnostics: {
        reason: 'field_not_org_managed_dropdown',
      },
    };
  }

  const descriptor = describeManagedDropdownInstance(fieldMetadata);
  const managedOptionSetId = normalizeString(
    descriptor?.managedOptionSetId,
    ''
  );
  const optionsSetUFI = normalizeString(
    descriptor?.optionsSetUFI || managedOptionSetId,
    ''
  );

  if (!managedOptionSetId) {
    return {
      status: 'error',
      field: descriptor,
      optionsSetUFI,
      managedOptionSetId: null,
      options: [],
      rawOptionSet: null,
      diagnostics: {
        reason: 'missing_managed_option_set_id',
      },
      error:
        '[managedFieldRuntime] Managed dropdown field is missing managedOptionSetId.',
    };
  }

  try {
    const response = await getManagedOptionSetById({
      organizationId,
      optionsSetId: managedOptionSetId,
    });

    if (response.status !== 'success' || !response.data) {
      return {
        status: 'not_found',
        field: descriptor,
        optionsSetUFI,
        managedOptionSetId,
        options: [],
        rawOptionSet: null,
        diagnostics: {
          reason: 'managed_option_set_not_found',
        },
      };
    }

    const rawOptionSet = response.data;

    // IMPORTANT:
    // Runtime visibility is decided centrally in managedFields.js via:
    //   • isManagedOptionCurrentlyLive(...)
    //   • resolveLiveManagedOptionsForRuntime(...)
    //
    // This runtime bridge should simply consume that result and forward the
    // runtime-safe option array without re-implementing lifecycle/date logic here.
    const resolvedOptions = resolveLiveManagedOptionsForRuntime(
      rawOptionSet,
      now
    );

    const diagnostics = {
      reason: 'resolved_successfully',
      totalOptionsInSource: Array.isArray(rawOptionSet?.options)
        ? rawOptionSet.options.length
        : 0,
      totalOptionsResolvedForRuntime: resolvedOptions.length,
      rawStatus: normalizeString(rawOptionSet?.status),
    };

    console.log('[managedFieldRuntime] Resolved managed dropdown instance options:', {
      organizationId,
      fieldId: descriptor?.fieldId,
      ufi: descriptor?.ufi,
      managedFieldId: descriptor?.managedFieldId,
      managedOptionSetId,
      optionsSetUFI,
      optionCount: resolvedOptions.length,
    });

    return {
      status: 'success',
      field: descriptor,
      optionsSetUFI,
      managedOptionSetId,
      options: resolvedOptions,
      rawOptionSet,
      diagnostics,
    };
  } catch (error) {
    console.error(
      '[managedFieldRuntime] Failed to resolve managed dropdown instance options:',
      error
    );

    return {
      status: 'error',
      field: descriptor,
      optionsSetUFI,
      managedOptionSetId,
      options: [],
      rawOptionSet: null,
      diagnostics: {
        reason: 'managed_option_resolution_error',
      },
      error: error?.message || String(error),
    };
  }
};

/**
 * Resolve ALL org-managed dropdown field instances in a form into a runtime-ready
 * dropdownOptions map compatible with your existing form runtime.
 *
 * OUTPUT
 *  {
 *    status: 'success',
 *    dropdownOptionsMap: {
 *      [optionsSetUFI]: [{ optionId, label, value, hint, order }]
 *    },
 *    rawOptionSetsBySetId: {
 *      [managedOptionSetId]: { ...rawOptionSetDoc }
 *    },
 *    diagnostics: {
 *      resolved: [...],
 *      skipped: [...],
 *      errors: [...]
 *    }
 *  }
 *
 * IMPORTANT
 *  • The map is keyed by optionsSetUFI to stay compatible with:
 *      - MultiAnswerFieldContext
 *      - FieldRenderer
 *      - SingleSelectFieldComponent
 *      - MultiSelectFieldComponent
 *  • If multiple field instances reference the same managed option set,
 *    they intentionally reuse the same optionsSetUFI-backed list.
 */
export const resolveManagedDropdownOptionsMapForForm = async ({
  organizationId,
  fieldsMetadata,
  now = new Date(),
}) => {
  if (!normalizeString(organizationId)) {
    throw new Error(
      '[managedFieldRuntime] organizationId is required to resolve form-managed dropdown options.'
    );
  }

  const candidates = buildManagedDropdownCandidates(fieldsMetadata || {});
  const dropdownOptionsMap = {};
  const rawOptionSetsBySetId = {};
  const diagnostics = {
    resolved: [],
    skipped: [],
    errors: [],
  };

  // Optimization:
  // If many field instances point to the same managed option set, resolve once and reuse.
  const cacheByManagedOptionSetId = new Map();

  for (const fieldMeta of candidates) {
    const descriptor = describeManagedDropdownInstance(fieldMeta);
    const managedOptionSetId = normalizeString(
      descriptor?.managedOptionSetId,
      ''
    );
    const optionsSetUFI = normalizeString(
      descriptor?.optionsSetUFI || managedOptionSetId,
      ''
    );

    if (!managedOptionSetId || !optionsSetUFI) {
      diagnostics.errors.push({
        fieldId: descriptor?.fieldId || null,
        ufi: descriptor?.ufi || null,
        managedFieldId: descriptor?.managedFieldId || null,
        managedOptionSetId: managedOptionSetId || null,
        optionsSetUFI: optionsSetUFI || null,
        reason: 'missing_managed_option_set_reference',
      });
      continue;
    }

    if (cacheByManagedOptionSetId.has(managedOptionSetId)) {
      const cached = cacheByManagedOptionSetId.get(managedOptionSetId);

      dropdownOptionsMap[optionsSetUFI] = toArraySafe(cached?.options);

      if (cached?.rawOptionSet) {
        rawOptionSetsBySetId[managedOptionSetId] = cached.rawOptionSet;
      }

      diagnostics.resolved.push({
        fieldId: descriptor?.fieldId,
        ufi: descriptor?.ufi,
        managedFieldId: descriptor?.managedFieldId,
        managedOptionSetId,
        optionsSetUFI,
        optionCount: toArraySafe(cached?.options).length,
        source: 'cache',
      });

      continue;
    }

    const result = await resolveManagedDropdownInstanceOptions({
      organizationId,
      fieldMetadata: fieldMeta,
      now,
    });

    if (result.status === 'success') {
      dropdownOptionsMap[optionsSetUFI] = toArraySafe(result.options);

      cacheByManagedOptionSetId.set(managedOptionSetId, {
        options: result.options,
        rawOptionSet: result.rawOptionSet,
      });

      if (result.rawOptionSet) {
        rawOptionSetsBySetId[managedOptionSetId] = result.rawOptionSet;
      }

      diagnostics.resolved.push({
        fieldId: descriptor?.fieldId,
        ufi: descriptor?.ufi,
        managedFieldId: descriptor?.managedFieldId,
        managedOptionSetId,
        optionsSetUFI,
        optionCount: toArraySafe(result.options).length,
        source: 'firestore',
      });

      continue;
    }

    if (result.status === 'not_found') {
      diagnostics.skipped.push({
        fieldId: descriptor?.fieldId,
        ufi: descriptor?.ufi,
        managedFieldId: descriptor?.managedFieldId,
        managedOptionSetId,
        optionsSetUFI,
        reason: 'managed_option_set_not_found',
      });
      continue;
    }

    if (result.status === 'not_applicable') {
      diagnostics.skipped.push({
        fieldId: descriptor?.fieldId || null,
        ufi: descriptor?.ufi || null,
        managedFieldId: descriptor?.managedFieldId || null,
        managedOptionSetId,
        optionsSetUFI,
        reason: result?.diagnostics?.reason || 'not_applicable',
      });
      continue;
    }

    diagnostics.errors.push({
      fieldId: descriptor?.fieldId,
      ufi: descriptor?.ufi,
      managedFieldId: descriptor?.managedFieldId,
      managedOptionSetId,
      optionsSetUFI,
      reason: result?.error || 'managed_option_resolution_error',
    });
  }

  console.log('[managedFieldRuntime] Resolved managed dropdown options map for form:', {
    organizationId,
    candidateCount: candidates.length,
    resolvedCount: diagnostics.resolved.length,
    skippedCount: diagnostics.skipped.length,
    errorCount: diagnostics.errors.length,
    optionSets: Object.keys(dropdownOptionsMap),
  });

  return {
    status: 'success',
    dropdownOptionsMap,
    rawOptionSetsBySetId,
    diagnostics,
  };
};

/**
 * Merge local dropdownOptions with org-managed dropdown options into one
 * runtime-compatible map.
 *
 * RULES
 *  • Local field option sets remain untouched
 *  • Org-managed option sets are overlaid onto the merged map
 *  • If a managed field reuses an optionsSetUFI that already exists locally,
 *    the org-managed value wins ONLY for that key
 *
 * WHY
 *  • This gives the caller a deterministic merged map for runtime rendering
 *    without mutating the original local dropdownOptions object.
 */
export const mergeLocalAndManagedDropdownOptions = ({
  localDropdownOptions = {},
  managedDropdownOptionsMap = {},
}) => {
  const merged = {
    ...(isObject(localDropdownOptions) ? localDropdownOptions : {}),
    ...(isObject(managedDropdownOptionsMap) ? managedDropdownOptionsMap : {}),
  };

  return merged;
};

/**
 * Validate whether a currently selected value is still selectable under the
 * current resolved runtime option set.
 *
 * This is helpful later for:
 *  • dependency health checks
 *  • preset health checks
 *  • builder/admin warnings for expired/inactive options
 *
 * OUTPUT
 *  {
 *    valid: boolean,
 *    invalidOptionIds: [],
 *  }
 */
export const validateSelectedDropdownValueAgainstRuntimeOptions = ({
  fieldMetadata,
  selectedValue,
  runtimeDropdownOptionsMap,
}) => {
  if (!fieldMetadata || typeof fieldMetadata !== 'object') {
    return {
      valid: false,
      invalidOptionIds: [],
    };
  }

  const optionsSetUFI = normalizeString(fieldMetadata?.optionsSetUFI, '');
  const runtimeOptions = toArraySafe(runtimeDropdownOptionsMap?.[optionsSetUFI]);
  const validOptionIds = new Set(
    runtimeOptions.map((opt) => normalizeString(opt?.optionId)).filter(Boolean)
  );

  if (isMultiSelectFieldType(fieldMetadata)) {
    const ids = Array.isArray(selectedValue)
      ? selectedValue.map((v) => normalizeString(v)).filter(Boolean)
      : [];

    const invalidOptionIds = ids.filter((id) => !validOptionIds.has(id));

    return {
      valid: invalidOptionIds.length === 0,
      invalidOptionIds,
    };
  }

  const singleId = normalizeString(selectedValue, '');
  if (!singleId) {
    return {
      valid: true,
      invalidOptionIds: [],
    };
  }

  return {
    valid: validOptionIds.has(singleId),
    invalidOptionIds: validOptionIds.has(singleId) ? [] : [singleId],
  };
};

/**
 * Build a compact diagnostics snapshot for all managed dropdown field instances
 * present in the current form metadata.
 *
 * This is useful later for:
 *  • admin warnings
 *  • builder-side badges
 *  • troubleshooting inactive/expired references
 */
export const buildManagedDropdownDiagnosticsSnapshot = ({
  fieldsMetadata,
}) => {
  const candidates = buildManagedDropdownCandidates(fieldsMetadata || {});

  return candidates.map((fieldMeta) => describeManagedDropdownInstance(fieldMeta));
};

/**
 * Build a dependency-health snapshot for managed dropdown field instances.
 *
 * WHY THIS EXISTS
 *  • Your dependency graph stays form-scoped
 *  • A dependency may continue to reference optionIds that are no longer live
 *  • We should NOT delete those rules automatically
 *  • But we DO need a safe way to report them later in builder/admin tooling
 *
 * INPUT
 *  • fieldsMetadata
 *  • dropdownDependencies
 *  • runtimeDropdownOptionsMap
 *
 * OUTPUT
 *  [
 *    {
 *      fieldId,
 *      ufi,
 *      optionsSetUFI,
 *      invalidDriverOptionIds: [...],
 *      invalidDependentOptionIds: [...],
 *    }
 *  ]
 */
export const buildManagedDependencyHealthSnapshot = ({
  fieldsMetadata,
  dropdownDependencies = {},
  runtimeDropdownOptionsMap = {},
}) => {
  const candidates = buildManagedDropdownCandidates(fieldsMetadata || {});
  const snapshot = [];

  for (const fieldMeta of candidates) {
    const descriptor = describeManagedDropdownInstance(fieldMeta);
    const optionsSetUFI = normalizeString(descriptor?.optionsSetUFI, '');

    if (!optionsSetUFI) continue;

    const dependencyMapForSet = isObject(dropdownDependencies?.[optionsSetUFI])
      ? dropdownDependencies[optionsSetUFI]
      : {};

    const runtimeOptions = toArraySafe(runtimeDropdownOptionsMap?.[optionsSetUFI]);
    const validRuntimeIds = new Set(
      runtimeOptions.map((opt) => normalizeString(opt?.optionId)).filter(Boolean)
    );

    const invalidDriverOptionIds = [];
    const invalidDependentOptionIds = [];

    Object.entries(dependencyMapForSet).forEach(([driverOptionId, conf]) => {
      const normalizedDriverId = normalizeString(driverOptionId, '');

      if (normalizedDriverId && !validRuntimeIds.has(normalizedDriverId)) {
        invalidDriverOptionIds.push(normalizedDriverId);
      }

      const dependentIds = Array.isArray(conf?.dependentOptionIds)
        ? conf.dependentOptionIds
        : [];

      dependentIds.forEach((depId) => {
        const normalizedDepId = normalizeString(depId, '');
        if (normalizedDepId && !validRuntimeIds.has(normalizedDepId)) {
          invalidDependentOptionIds.push(normalizedDepId);
        }
      });
    });

    snapshot.push({
      fieldId: descriptor?.fieldId || null,
      ufi: descriptor?.ufi || null,
      managedFieldId: descriptor?.managedFieldId || null,
      optionsSetUFI,
      invalidDriverOptionIds: Array.from(new Set(invalidDriverOptionIds)),
      invalidDependentOptionIds: Array.from(new Set(invalidDependentOptionIds)),
    });
  }

  return snapshot;
};

/**
 * Convenience helper:
 * returns true if the form contains at least one org-managed dropdown instance.
 */
export const formContainsManagedDropdowns = ({
  fieldsMetadata,
}) => {
  return buildManagedDropdownCandidates(fieldsMetadata || {}).length > 0;
};

/**
 * Convenience helper:
 * Build a runtime option lookup map keyed first by optionsSetUFI and then optionId.
 *
 * WHY
 *  • Presets and diagnostics often need fast access to "is this option currently live?"
 *  • This keeps that logic centralized rather than repeated in consumers later
 */
export const buildManagedRuntimeOptionLookup = ({
  runtimeDropdownOptionsMap = {},
}) => {
  const lookup = {};

  if (!isObject(runtimeDropdownOptionsMap)) return lookup;

  Object.entries(runtimeDropdownOptionsMap).forEach(([optionsSetUFI, options]) => {
    lookup[optionsSetUFI] = buildOptionLookupFromRuntimeOptions(options);
  });

  return lookup;
};